import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { GoogleLogo, Sparkle, ShieldCheck, LockKey } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const denied = params.get("error");

  useEffect(() => {
    api.authConfig().then(setConfig).catch(() => setConfig({ configured: false }));
  }, []);

  const signIn = () => {
    if (!config?.google_client_id) return;
    setLoading(true);
    const state = Math.random().toString(36).slice(2);
    sessionStorage.setItem("aria_oauth_state", state);
    const redirectUri = window.location.origin + "/auth/google";
    const params = new URLSearchParams({
      client_id: config.google_client_id,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: [
        "openid",
        "https://www.googleapis.com/auth/userinfo.email",
        "https://www.googleapis.com/auth/userinfo.profile",
        "https://www.googleapis.com/auth/gmail.readonly",
        "https://www.googleapis.com/auth/calendar.readonly",
      ].join(" "),
      access_type: "offline",
      include_granted_scopes: "true",
      prompt: "consent select_account",
      state,
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-background">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-8">
          <div className="h-14 w-14 rounded-2xl bg-slate-900 flex items-center justify-center mb-5">
            <Sparkle weight="fill" className="text-white" size={28} />
          </div>
          <h1 className="font-heading text-4xl font-light tracking-tight text-slate-900">Auppo</h1>
          <p className="text-slate-500 mt-3 max-w-sm leading-relaxed">Your private AI executive assistant. Sign in to continue.</p>
        </div>

        <div className="bg-white border border-border rounded-2xl p-6 sm:p-8">
          {denied === "unauthorized" && (
            <div data-testid="login-denied" className="flex items-start gap-2 text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded-xl px-3.5 py-2.5 mb-5">
              <LockKey weight="fill" size={16} className="mt-0.5 shrink-0" />
              <span>That Google account isn't authorized.{config?.allowed_email ? <> Please sign in with <strong>{config.allowed_email}</strong>.</> : " This app is private."}</span>
            </div>
          )}

          {config && !config.configured ? (
            <div data-testid="login-not-configured" className="text-center text-sm text-slate-500 py-4">
              Google sign-in isn't configured yet. Add your Google OAuth Client ID & Secret to enable login.
            </div>
          ) : (
            <Button
              data-testid="google-signin-btn"
              onClick={signIn}
              disabled={!config || loading}
              className="w-full h-12 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-[15px] font-medium gap-2.5"
            >
              <GoogleLogo weight="bold" size={20} />
              {loading ? "Redirecting…" : "Continue with Google"}
            </Button>
          )}

          <div className="flex items-center justify-center gap-1.5 mt-4 text-xs text-slate-400">
            <ShieldCheck size={14} /> Private · single-account access
          </div>
        </div>
      </motion.div>
    </div>
  );
}
